import { ComingSoonPanel, IconPlus, PageHeader } from '@shared/ui';

/**
 * Creating a client is not offered yet.
 *
 * ## Why this is a screen and not a disabled button
 *
 * A greyed-out "New client" says only that something is unavailable. The
 * question an operator actually has — *then how do I get a client created?* —
 * needs a sentence, and a sentence needs somewhere to live. So the entry point
 * stays clickable and answers itself.
 *
 * ## What blocks it
 *
 * Upstream, not here: a client is a tenant plus a federated owner, and
 * VeevartBackend has no single call that creates both. Neither
 * `POST /internal/provisioning/tenant` nor `/internal/provisioning/organizations`
 * can do it — both accept `admin: { email }` only, which provisions into the
 * staff pool, while the owner this product wants signs in with a username
 * against the federation pool.
 *
 * So creating a client means two writes that cannot be made atomic from here,
 * with a half-created state and no compensating delete. Shipping the list
 * without that is not a smaller feature; it is the same feature without a
 * failure mode we cannot clean up. See `apps/api/src/modules/clients/README.md`.
 *
 * ## The wizard that used to be here
 *
 * A working three-step `NewClientWizard` and its `wizard-state` — draft, step
 * completion, and a failure taxonomy that mapped each half-created state back
 * to the step it came from — were deleted rather than left unrouted. Unrouted
 * and unrenderable by any test, they would have been broken by the next change
 * to `ClientTarget` or `form.tsx` with nothing to notice, and a reader would
 * have had to work out whether they still described anything.
 *
 * They are in the history, at the parent of the commit that removed them —
 * `git log --diff-filter=D -- apps/web/src/features/clients/components` finds
 * it. Recovering them is a checkout; keeping them compiling for months is not
 * free.
 */
export function NewClientComingSoon() {
    return (
        <>
            <PageHeader
                eyebrow="Tenants · Client"
                title="New client"
                description="Creating a client from the Backoffice is not available yet."
            />
            <ComingSoonPanel
                icon={<IconPlus className="size-6" />}
                title="Creating a client needs one upstream call"
                blurb="A client is a tenant plus its first owner, and today those are two separate writes against two services — a failure between them leaves a client with no way in. This opens once VeevartBackend can create both together. Clients that arrive through Salesforce keep registering themselves in the meantime."
            />
        </>
    );
}
